import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../axiosinstance";
import Toaster from "../context/Toaster";
import { navigateTo } from "../utils/navigateTo";

interface AuthState {
  loading: boolean;
  error: string | null;
  token: string | null;
  userId: string | null;
  type: string | null;
  login: boolean;
}

const initialState: AuthState = {
  loading: false,
  error: null,
  token: null,
  userId: null,
  type: null,
  login: false,
};

// Async thunk for login
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (
    { email, password }: { email: string; password: string },
    thunkAPI
  ) => {
    try {
      const response = await axiosInstance.post("/auth/login", {
        email,
        password,
      });

      // Use your actual response structure
      const { userId, token, type, login } = response.data;

      localStorage.setItem("authToken", token);
      if (type === "superadmin" || type === "admin") {
        navigateTo("/admindashboard");

        const { showToast } = Toaster();

        showToast("Login successful!", { type: "success" });
      }

      return { token, userId, type, login };
    } catch (err: any) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.error || "Something went wrong, please try again."
      );
    }
  }
);

// Admin OTP login endpoints — update these if the backend uses different paths
export const ADMIN_REQUEST_OTP_URL = "/auth/admin/request-otp";
export const ADMIN_VERIFY_OTP_URL = "/auth/admin/verify-otp";

// Sends a 6-digit login code to an admin's email
export const requestAdminOtp = (email: string) =>
  axiosInstance.post(ADMIN_REQUEST_OTP_URL, { email });

// Verifies the admin's OTP and stores the session
export const verifyAdminOtp = createAsyncThunk(
  "auth/verifyAdminOtp",
  async ({ email, otp }: { email: string; otp: string }, thunkAPI) => {
    try {
      const response = await axiosInstance.post(ADMIN_VERIFY_OTP_URL, {
        email,
        otp,
      });

      const { userId, token, type, login } = response.data;

      localStorage.setItem("authToken", token);

      return { token, userId, type, login };
    } catch (err: any) {
      return thunkAPI.rejectWithValue({
        status: err.response?.status,
        message:
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Something went wrong, please try again.",
      });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      Object.assign(state, initialState);
      localStorage.removeItem("authToken");
      localStorage.removeItem("userId");
      localStorage.removeItem("userType");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loginUser.fulfilled,
        (
          state,
          action: PayloadAction<{
            token: string;
            userId: string;
            type: string;
            login: boolean;
          }>
        ) => {
          state.loading = false;
          state.token = action.payload.token;
          state.userId = action.payload.userId;
          state.type = action.payload.type;
          state.login = action.payload.login;
        }
      )
      .addCase(loginUser.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(verifyAdminOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyAdminOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.userId = action.payload.userId;
        state.type = action.payload.type;
        state.login = action.payload.login;
      })
      .addCase(verifyAdminOtp.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload?.message ?? null;
      });
  },
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
